import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';

// Import components
import './components/landing/index.js';

// Silent HMR setup - enables auto-refresh without console spam
if (import.meta.hot) {
  import.meta.hot.accept();
  import.meta.hot.dispose(() => {
    // Silent cleanup
  });
}

@customElement('pristinemaint-app')
export class PristineMaintApp extends LitElement {
  @state()
  private currentRoute: string = this.resolveRoute();

  @state()
  private targetSection: string | null = null;

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      font-family: var(--sl-font-sans);
      background: var(--sl-color-neutral-50);
    }
  `;

  private readonly handlePopState = () => {
    this.currentRoute = this.resolveRoute();
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this.handlePopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handlePopState);
    super.disconnectedCallback();
  }

  private resolveRoute(): string {
    const path = window.location.pathname;
    if (path === '/features') return '/features';
    if (path === '/pricing') return '/pricing';
    return '/';
  }

  private handleNavigate(event: CustomEvent<{ route: string; section?: string }>) {
    event.stopPropagation();
    const { route, section } = event.detail;
    this.targetSection = section ?? null;

    if (route !== this.currentRoute) {
      window.history.pushState({}, '', route);
      this.currentRoute = this.resolveRoute();
    } else if (route === '/' && section) {
      this.requestUpdate();
    }
  }

  private handleSectionConsumed() {
    this.targetSection = null;
  }

  render() {
    return html`
      ${this.renderCurrentRoute()}
    `;
  }

  private renderCurrentRoute() {
    switch (this.currentRoute) {
      case '/features':
        return html`<pristinemaint-features @navigate=${this.handleNavigate}></pristinemaint-features>`;
      case '/pricing':
        return html`<pristinemaint-pricing @navigate=${this.handleNavigate}></pristinemaint-pricing>`;
      default:
        return html`<pristinemaint-landing
          .sectionRequest=${this.targetSection}
          @navigate=${this.handleNavigate}
          @section-request-consumed=${this.handleSectionConsumed}
        ></pristinemaint-landing>`;
    }
  }
}

// Initialize the app
document.addEventListener('DOMContentLoaded', () => {
  // Check if app already exists to prevent duplicates
  if (!document.querySelector('pristinemaint-app')) {
    const app = document.createElement('pristinemaint-app');
    document.getElementById('app')?.appendChild(app);
  }
});
